import PropTypes from 'prop-types';
import TilePicker from './TilePicker';

/**
 * What the next click will do.
 *
 * THE TILE LIST NOW COMES FROM THE ART, which is what the previous version of
 * this comment said should happen once there was any. `TilePicker` reads the
 * world's own tileset and the manifest beside it, so drawing a new tile into
 * the sheet puts it in the palette with no change here.
 *
 * WALLS ARE WHAT COLLISION READS. Any non-zero tile on that layer is solid, so
 * painting the wall tile onto `floor` looks identical in game and stops nobody.
 * The layer selector says which layer is being painted for exactly that reason —
 * and it matters MORE now that tiles look like scenery, because a hedge painted
 * on `floor` reads as an obstacle to a player and lets them walk straight
 * through it.
 */

const LAYERS = [
  { id: 'floor', name: 'Floor', hint: 'Ground. Drawn under everyone.' },
  { id: 'walls', name: 'Walls', hint: 'Any tile here is SOLID.' },
  { id: 'above', name: 'Above', hint: 'Drawn over players — roofs, canopy.' },
];

const TOOLS = [
  { id: 'paint', name: 'Paint', hint: 'Drag to fill tiles.' },
  { id: 'erase', name: 'Erase', hint: 'Drag to clear tiles on this layer.' },
  { id: 'zone', name: 'Building', hint: 'Drag out a shop or house. Its walls are generated; click one to rename it.' },
  { id: 'spawn', name: 'Spawn', hint: 'Where players appear.' },
  { id: 'portal', name: 'Portal', hint: 'A doorway to another world.' },
  { id: 'sign', name: 'Sign', hint: 'Text players can read.' },
  { id: 'pick', name: 'Remove', hint: 'Click a spawn, portal or sign to delete it.' },
];

const button = (active) => `rounded px-2 py-1 text-xs ${
  active
    ? 'bg-blue-600 text-white'
    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
}`;

function ToolPalette({
  tool, layer, brushGid, zoom, showUnreachable, showArt, sheet, tileset,
  onTool, onLayer, onBrush, onZoom, onToggleUnreachable, onShowArt,
}) {
  const painting = tool === 'paint' || tool === 'erase';

  return (
    <div className="space-y-4 text-sm">
      <div>
        <p className="mb-1 font-medium text-gray-700 dark:text-gray-300">Tool</p>
        <div className="flex flex-wrap gap-1">
          {TOOLS.map((t) => (
            <button key={t.id} type="button" title={t.hint} onClick={() => onTool(t.id)} className={button(tool === t.id)}>
              {t.name}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          {TOOLS.find((t) => t.id === tool)?.hint}
        </p>
      </div>

      {/* Only meaningful while painting — a spawn point has no layer. */}
      {painting && (
        <div>
          <p className="mb-1 font-medium text-gray-700 dark:text-gray-300">Layer</p>
          <div className="flex flex-wrap gap-1">
            {LAYERS.map((l) => (
              <button key={l.id} type="button" title={l.hint} onClick={() => onLayer(l.id)} className={button(layer === l.id)}>
                {l.name}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {LAYERS.find((l) => l.id === layer)?.hint}
          </p>
        </div>
      )}

      {tool === 'paint' && (
        <div>
          <p className="mb-1 font-medium text-gray-700 dark:text-gray-300">Tile</p>
          <TilePicker
            sheet={sheet}
            tileset={tileset}
            brushGid={brushGid}
            onBrush={onBrush}
          />
        </div>
      )}

      <div>
        <p className="mb-1 font-medium text-gray-700 dark:text-gray-300">View</p>
        <div className="flex flex-wrap gap-1">
          {/*
            Blocks is not the "old" view kept out of nostalgia. Flat colour is
            genuinely the clearer way to read STRUCTURE — what is solid, where
            the zones are, which areas are unreachable — and those overlays sit
            on top of whatever is underneath. Art answers a different question:
            what will a player actually see.
          */}
          <button type="button" onClick={() => onShowArt(true)} className={button(showArt)}>Art</button>
          <button type="button" onClick={() => onShowArt(false)} className={button(!showArt)}>Blocks</button>
        </div>
        {showArt && zoom < 8 && (
          <p className="mt-1 text-xs text-amber-500">
            Zoomed out this far, tiles are a few pixels each — Blocks is easier to read.
          </p>
        )}
      </div>

      <div>
        <label className="block font-medium text-gray-700 dark:text-gray-300" htmlFor="zoom">
          Zoom
          <input
            id="zoom"
            type="range"
            min="4"
            max="32"
            value={zoom}
            onChange={(e) => onZoom(Number(e.target.value))}
            className="mt-1 w-full"
          />
        </label>
      </div>

      <label className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400" htmlFor="unreachable">
        <input
          id="unreachable"
          type="checkbox"
          checked={showUnreachable}
          onChange={(e) => onToggleUnreachable(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          <span className="font-medium">Shade unreachable areas</span>
          <br />
          Anywhere a player cannot walk to from a spawn point — a sealed room, or
          a corridor narrower than they are.
        </span>
      </label>
    </div>
  );
}

ToolPalette.propTypes = {
  tool: PropTypes.string.isRequired,
  layer: PropTypes.string.isRequired,
  brushGid: PropTypes.number.isRequired,
  zoom: PropTypes.number.isRequired,
  showUnreachable: PropTypes.bool.isRequired,
  showArt: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  sheet: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  tileset: PropTypes.object,
  onTool: PropTypes.func.isRequired,
  onLayer: PropTypes.func.isRequired,
  onBrush: PropTypes.func.isRequired,
  onZoom: PropTypes.func.isRequired,
  onToggleUnreachable: PropTypes.func.isRequired,
  onShowArt: PropTypes.func.isRequired,
};

ToolPalette.defaultProps = { tileset: null };

export default ToolPalette;
